import type { CampaignCreationCapabilities } from "../schemas/campaign-creation";
import type { SmartCreationReviewWorkspace } from "../schemas/smart-creation-review";
import { SmartCreationReviewError, type SmartCreationReviewErrorCode } from "./errors";

export function round(value: number, decimals: number): number {
  const scale = 10 ** decimals;
  return Math.round(value * scale) / scale;
}

export function validateNumeric(
  value: number,
  limits: { min: number; max: number; decimals: number },
  code: SmartCreationReviewErrorCode,
) {
  if (value < limits.min || value > limits.max || round(value, limits.decimals) !== value) {
    throw new SmartCreationReviewError(
      422,
      code,
      "The value is outside current marketplace limits",
    );
  }
}

export function assertWorkspaceLimits(
  workspace: SmartCreationReviewWorkspace,
  capabilities: CampaignCreationCapabilities,
): void {
  const includedCampaigns = workspace.graph.campaigns.filter(
    (campaign) => !campaign.excluded.effective,
  );
  if (includedCampaigns.length === 0) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_all_campaigns_excluded",
      "At least one Campaign must remain included",
    );
  }
  if (workspace.draftContent.structure === "precise") {
    for (const campaign of workspace.graph.campaigns) {
      const groups = workspace.graph.adGroups.filter(
        (group) => group.campaignLineageKey === campaign.ref.lineageKey,
      );
      if (groups.length !== 1 || groups.some((group) => group.targetLineageKeys.length !== 1)) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_target_invalid",
          "Precise Campaigns must retain one Ad Group and one positive Target",
        );
      }
    }
  }
  for (const adGroup of workspace.graph.adGroups) {
    const campaign = workspace.graph.campaigns.find(
      (candidate) => candidate.ref.lineageKey === adGroup.campaignLineageKey,
    );
    if (campaign?.excluded.effective) continue;
    const includedTargetCount = adGroup.targetLineageKeys.filter((lineageKey) => {
      const target = workspace.graph.targets.find(
        (candidate) => candidate.ref.lineageKey === lineageKey,
      );
      return target && !target.excluded.effective;
    }).length;
    if (includedTargetCount === 0) {
      throw new SmartCreationReviewError(
        422,
        "smart_creation.review_target_invalid",
        "Every included Ad Group must retain at least one included Target",
      );
    }
  }
  // Negatives are submitted with an Ad Group's Campaign creation payload.
  // Marketplace limits therefore apply independently to every payload, not
  // to the aggregate Review Workspace.
  const negativeCountsByPayload = new Map<string, { keywords: number; products: number }>();
  for (const negative of workspace.graph.negatives) {
    const counts = negativeCountsByPayload.get(negative.adGroupLineageKey) ?? {
      keywords: 0,
      products: 0,
    };
    if (negative.type === "keyword") counts.keywords += 1;
    else counts.products += 1;
    negativeCountsByPayload.set(negative.adGroupLineageKey, counts);
  }
  for (const [adGroupLineageKey, counts] of negativeCountsByPayload) {
    if (
      counts.keywords > capabilities.limits.maxNegativeKeywords ||
      counts.products > capabilities.limits.maxNegativeProducts
    ) {
      throw new SmartCreationReviewError(
        422,
        "smart_creation.review_negative_invalid",
        "Negative count exceeds current marketplace limits for one Campaign payload",
        { adGroupLineageKey },
      );
    }
  }
}
